/**
 * Vista de Calendario para Entrevistas RH
 * Muestra un calendario semanal con las entrevistas programadas
 */

import React, { useState, useMemo } from 'react'
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  MapPin,
  Video,
  CheckCircle,
  XCircle,
  Clock,
  User
} from 'lucide-react'
import type { HRInterview } from '@/types/hrInterview'
import { nombreDeUsuario } from '@/shared/lib/nombreDeUsuario'
import { parseDateSafe } from '@/shared/lib/dateUtils'
import { Badge } from '@/shared/components/Badge'

interface HRInterviewsCalendarProps {
  interviews: HRInterview[]
  isLoading?: boolean
  onInterviewClick?: (interview: HRInterview) => void
}

type CalendarView = 'day' | 'week' | 'month'

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom']
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

// Horas del día (7 AM - 5 PM)
const HOURS = [
  '7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM',
  '1 PM', '2 PM', '3 PM', '4 PM'
]

export const HRInterviewsCalendar: React.FC<HRInterviewsCalendarProps> = ({
  interviews,
  isLoading = false,
  onInterviewClick
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [calendarView, setCalendarView] = useState<CalendarView>('month')

  // Obtener el primer día del mes
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  // Obtener el último día del mes
  const getLastDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)
  }

  // Obtener días del mes para el calendario
  const getDaysInMonth = (date: Date) => {
    const firstDay = getFirstDayOfMonth(date)
    const lastDay = getLastDayOfMonth(date)
    const days: (Date | null)[] = []

    // Días antes del primer día del mes
    const firstDayOfWeek = firstDay.getDay()
    const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
    
    for (let i = 0; i < startPadding; i++) {
      const prevDate = new Date(firstDay)
      prevDate.setDate(prevDate.getDate() - (startPadding - i))
      days.push(prevDate)
    }

    // Días del mes
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(date.getFullYear(), date.getMonth(), i))
    }

    return days
  }

  // Obtener los días de la semana actual
  const getWeekDays = (date: Date) => {
    const dayOfWeek = date.getDay()
    const monday = new Date(date)
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    monday.setDate(date.getDate() + diff)

    const days: Date[] = []
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday)
      day.setDate(monday.getDate() + i)
      days.push(day)
    }
    return days
  }

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate])
  const monthDays = useMemo(() => getDaysInMonth(currentDate), [currentDate])

  // Filtrar entrevistas para una fecha específica
  const getInterviewsForDate = (date: Date) => {
    return interviews.filter(interview => {
      const interviewDate = parseDateSafe(interview.fechaEntrevista)
      if (!interviewDate) return false
      return (
        interviewDate.getDate() === date.getDate() &&
        interviewDate.getMonth() === date.getMonth() &&
        interviewDate.getFullYear() === date.getFullYear()
      )
    })
  }

  // Obtener próximas entrevistas (ordenadas por fecha)
  const upcomingInterviews = useMemo(() => {
    const hoy = new Date()
    hoy.setHours(0, 0, 0, 0)
    return interviews
      .filter(interview => {
        const fecha = parseDateSafe(interview.fechaEntrevista)
        return !!fecha && fecha >= hoy
      })
      .sort((a, b) => {
        const fa = parseDateSafe(a.fechaEntrevista)?.getTime() ?? 0
        const fb = parseDateSafe(b.fechaEntrevista)?.getTime() ?? 0
        return fa - fb
      })
      .slice(0, 10)
  }, [interviews])

  // Agrupar próximas entrevistas por día
  const groupedUpcomingInterviews = useMemo(() => {
    const grouped: Record<string, HRInterview[]> = {}
    
    upcomingInterviews.forEach(interview => {
      const date = parseDateSafe(interview.fechaEntrevista)
      const dateKey = date
        ? date.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit' })
        : 'Sin fecha'
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(interview)
    })
    
    return grouped
  }, [upcomingInterviews])

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }



  const isToday = (date: Date) => {
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  const isSameMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth()
  }

  // Convertir hora de entrevista a posición en el calendario
  const getTimePosition = (time: string) => {
    const [hourStr, period] = time.split(' ')
    let hour = parseInt(hourStr.split(':')[0])
    
    if (period === 'PM' && hour !== 12) {
      hour += 12
    } else if (period === 'AM' && hour === 12) {
      hour = 0
    }
    
    return hour - 7 // Restamos 7 porque empezamos a las 7 AM
  }

  const getInterviewIcon = (interview: HRInterview) => {
    if (interview.modalidad === 'virtual') {
      return <Video className="w-3 h-3" />
    }
    return <MapPin className="w-3 h-3" />
  }

  const getInterviewStatusIcon = (interview: HRInterview) => {
    if (interview.decision === 'aprobado') {
      return <CheckCircle className="w-3 h-3 text-green-600" />
    } else if (interview.decision === 'rechazado') {
      return <XCircle className="w-3 h-3 text-red-600" />
    }
    return null
  }

  const getInterviewColorClasses = (interview: HRInterview) => {
    // Colorear por estatus de la decisión
    if (interview.decision === 'aprobado') {
      return 'bg-green-100 border-l-4 border-green-500'
    } else if (interview.decision === 'rechazado') {
      return 'bg-red-100 border-l-4 border-red-500'
    } else if (interview.decision === 'pendiente' || !interview.decision) {
      // Pendiente o sin decisión - color amarillo/naranja
      return 'bg-yellow-100 border-l-4 border-yellow-500'
    }
    return 'bg-gray-100 border-l-4 border-gray-500'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando calendario...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Panel izquierdo - Calendario mensual y próximas entrevistas */}
      <div className="w-80 space-y-4">
        {/* Mini calendario */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {MONTHS[currentDate.getMonth()]}
            </h3>
            <div className="flex gap-1">
              <button
                onClick={handlePrevMonth}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNextMonth}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS_OF_WEEK.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>

          {/* Días del mes */}
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((date, index) => {
              if (!date) return <div key={index} />
              
              const hasInterviews = getInterviewsForDate(date).length > 0
              const isCurrentMonth = isSameMonth(date)
              const isTodayDate = isToday(date)

              return (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentDate(date)
                  }}
                  className={`aspect-square flex items-center justify-center text-sm rounded-full transition-colors
                    ${!isTodayDate && isCurrentMonth ? 'text-gray-900 hover:bg-gray-100' : ''}
                    ${!isTodayDate && !isCurrentMonth ? 'text-gray-400' : ''}
                    ${hasInterviews && !isTodayDate ? 'font-semibold' : ''}`}
                  style={isTodayDate ? { backgroundColor: '#315FB9', color: '#FFFFFF', fontWeight: '600' } : {}}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>
        </div>

        {/* Próximas entrevistas */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Próximas entrevistas</h3>
          
          <div className="space-y-4">
            {Object.entries(groupedUpcomingInterviews).map(([dateKey, dayInterviews]) => (
              <div key={dateKey}>
                <p className="text-sm font-medium text-gray-700 mb-2 capitalize">{dateKey}</p>
                <div className="space-y-2">
                  {dayInterviews.map(interview => (
                    <button
                      key={interview.id}
                      onClick={() => onInterviewClick?.(interview)}
                      className="w-full text-left flex items-start gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="mt-0.5">
                        {getInterviewIcon(interview)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {interview.candidate.fullName}
                        </p>
                        <p className="text-xs text-gray-500">{interview.horaEntrevista}</p>
                        <p className="text-xs text-gray-500 capitalize">
                          {interview.candidate.jobRequest?.puestoSolicitar || 'Puesto no especificado'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            {Object.keys(groupedUpcomingInterviews).length === 0 && (
              <p className="text-sm text-gray-500">No hay entrevistas próximas</p>
            )}
          </div>
        </div>
      </div>

      {/* Panel derecho - Vista de calendario */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200">
        {/* Header con navegación y vista */}
        <div className="border-b border-gray-200 p-4 flex items-center justify-center">
          {/* Pestañas de vista - centradas */}
          <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
            <button
              onClick={() => setCalendarView('day')}
              className="px-4 py-1 text-sm font-medium rounded-md transition-colors hover:opacity-90"
              style={{ 
                backgroundColor: calendarView === 'day' ? '#315FB9' : 'transparent',
                color: calendarView === 'day' ? '#FFFFFF' : '#4B5563'
              }}
            >
              Día
            </button>
            <button
              onClick={() => setCalendarView('week')}
              className="px-4 py-1 text-sm font-medium rounded-md transition-colors hover:opacity-90"
              style={{ 
                backgroundColor: calendarView === 'week' ? '#315FB9' : 'transparent',
                color: calendarView === 'week' ? '#FFFFFF' : '#4B5563'
              }}
            >
              Semana
            </button>
            <button
              onClick={() => setCalendarView('month')}
              className="px-4 py-1 text-sm font-medium rounded-md transition-colors hover:opacity-90"
              style={{ 
                backgroundColor: calendarView === 'month' ? '#315FB9' : 'transparent',
                color: calendarView === 'month' ? '#FFFFFF' : '#4B5563'
              }}
            >
              Mes
            </button>
          </div>
        </div>

        {/* Vista semanal */}
        {calendarView === 'week' && (
          <div className="overflow-x-auto">
            {/* Header con días de la semana */}
            <div className="grid grid-cols-8 border-b border-gray-200 sticky top-0 bg-white">
              <div className="p-2"></div>
              {weekDays.map((day, index) => {
                const dayName = day.toLocaleDateString('es-ES', { weekday: 'long' })
                const dayNumber = day.getDate()
                const isTodayDate = isToday(day)

                return (
                  <div
                    key={index}
                    className={`p-3 text-center border-r border-gray-200 ${
                      isTodayDate ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="text-xs text-gray-600 capitalize mb-1">{dayName}</div>
                    <div
                      className={`text-2xl font-semibold ${
                        isTodayDate ? 'text-blue-600' : 'text-gray-900'
                      }`}
                    >
                      {dayNumber.toString().padStart(2, '0')}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Grid de horas y entrevistas */}
            <div className="relative">
              {HOURS.map((hour, hourIndex) => (
                <div key={hour} className="grid grid-cols-8 border-b border-gray-200">
                  {/* Columna de hora */}
                  <div className="p-2 text-xs text-gray-500 text-right pr-3 border-r border-gray-200">
                    {hour}
                  </div>

                  {/* Columnas de días */}
                  {weekDays.map((day, dayIndex) => {
                    const dayInterviews = getInterviewsForDate(day).filter(interview => {
                      const interviewHour = interview.horaEntrevista
                      const position = getTimePosition(interviewHour)
                      return position === hourIndex
                    })

                    return (
                      <div
                        key={dayIndex}
                        className={`min-h-[60px] border-r border-gray-200 p-1 ${
                          isToday(day) ? 'bg-blue-50/30' : ''
                        }`}
                      >
                        {dayInterviews.map(interview => (
                          <button
                            key={interview.id}
                            onClick={() => onInterviewClick?.(interview)}
                            className={`w-full text-left p-2 rounded text-xs mb-1 hover:shadow-md transition-shadow ${
                              getInterviewColorClasses(interview)
                            }`}
                          >
                            <div className="flex items-start justify-between gap-1 mb-1">
                              <div className="flex items-center gap-1">
                                {getInterviewIcon(interview)}
                                <span className="font-medium">
                                  {interview.horaEntrevista}
                                </span>
                              </div>
                              {getInterviewStatusIcon(interview)}
                            </div>
                            <p className="font-medium text-gray-900 truncate">
                              {interview.candidate.fullName}
                            </p>
                            <p className="text-gray-600 capitalize truncate">
                              {interview.candidate.jobRequest?.puestoSolicitar || 'Puesto'}
                            </p>
                          </button>
                        ))}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vista de Día */}
        {calendarView === 'day' && (
          <div className="overflow-y-auto max-h-[600px]">
            {/* Header del día seleccionado */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900">
                  {currentDate.toLocaleDateString('es-ES', { day: '2-digit' })}
                </h3>
                <p className="text-sm text-gray-600 capitalize">
                  {currentDate.toLocaleDateString('es-ES', { weekday: 'long', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Lista de entrevistas del día */}
            <div className="p-6">
              {(() => {
                const dayInterviews = getInterviewsForDate(currentDate)
                
                if (dayInterviews.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No hay entrevistas programadas
                      </h3>
                      <p className="text-gray-600">
                        No se encontraron entrevistas para este día
                      </p>
                    </div>
                  )
                }

                // Agrupar por hora
                const sortedInterviews = [...dayInterviews].sort((a, b) => {
                  const timeA = a.horaEntrevista
                  const timeB = b.horaEntrevista
                  return timeA.localeCompare(timeB)
                })

                return (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      {dayInterviews.length} {dayInterviews.length === 1 ? 'Entrevista' : 'Entrevistas'} programadas
                    </h4>
                    
                    {sortedInterviews.map(interview => (
                      <button
                        key={interview.id}
                        onClick={() => onInterviewClick?.(interview)}
                        className={`w-full text-left rounded-xl p-5 transition-all hover:shadow-lg ${
                          getInterviewColorClasses(interview)
                        }`}
                      >
                        {/* Header de la tarjeta */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg">
                              <Clock className="w-5 h-5 text-gray-700" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-900">
                                {interview.horaEntrevista}
                              </p>
                              <p className="text-xs text-gray-600">
                                Duración estimada: 1 hora
                              </p>
                            </div>
                          </div>
                          {getInterviewStatusIcon(interview)}
                        </div>

                        {/* Información del candidato */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Candidato</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {interview.candidate.fullName}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Puesto</p>
                              <p className="text-sm font-medium text-gray-900 capitalize">
                                {interview.candidate.jobRequest?.puestoSolicitar || 'No especificado'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Modalidad</p>
                              <div className="flex items-center gap-2">
                                {getInterviewIcon(interview)}
                                <p className="text-sm font-medium text-gray-900 capitalize">
                                  {interview.modalidad}
                                </p>
                              </div>
                            </div>
                          </div>

                          {interview.ubicacionLink && (
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                {interview.modalidad === 'virtual' ? 'Link de reunión' : 'Ubicación'}
                              </p>
                              <p className="text-sm text-blue-600 truncate">
                                {interview.ubicacionLink}
                              </p>
                            </div>
                          )}

                          {/* Entrevistador */}
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Entrevistador</p>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-500" />
                              <p className="text-sm font-medium text-gray-900">
                                {nombreDeUsuario(
                                  interview.entrevistador ?? interview.entrevistadorHistorico,
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Resultado si existe */}
                          {interview.resultadoEntrevistaRh && (
                            <div>
                              <p className="text-sm text-gray-600 mb-2">Resultado</p>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all"
                                    style={{ width: `${interview.resultadoEntrevistaRh * 10}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-gray-900">
                                  {interview.resultadoEntrevistaRh}/10
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        {/* Vista de Mes */}
        {calendarView === 'month' && (
          <div className="p-6">
            {/* Header del mes */}
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-bold text-gray-900">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
            </div>

            {/* Grid del calendario mensual */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Días de la semana */}
              <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="p-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Grid de días */}
              <div className="grid grid-cols-7">
                {monthDays.map((date, index) => {
                  if (!date) return <div key={index} className="border-r border-b border-gray-200 min-h-[120px]" />
                  
                  const dayInterviews = getInterviewsForDate(date)
                  const isCurrentMonth = isSameMonth(date)
                  const isTodayDate = isToday(date)

                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentDate(date)
                        setCalendarView('day')
                      }}
                      className={`border-r border-b border-gray-200 min-h-[120px] p-2 text-left hover:bg-gray-50 transition-colors ${
                        !isCurrentMonth ? 'bg-gray-50' : ''
                      } ${isTodayDate ? 'bg-blue-50' : ''}`}
                    >
                      {/* Número del día */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-semibold ${
                          isTodayDate ? 'text-blue-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {date.getDate()}
                        </span>
                        {dayInterviews.length > 0 && (
                          <Badge color="morado">{dayInterviews.length}</Badge>
                        )}
                      </div>

                      {/* Lista de entrevistas del día (máximo 3) */}
                      <div className="space-y-1">
                        {dayInterviews.slice(0, 3).map(interview => (
                          <div
                            key={interview.id}
                            className={`text-xs p-1.5 rounded truncate ${
                              getInterviewColorClasses(interview)
                            }`}
                          >
                            <div className="flex items-center gap-1">
                              {getInterviewIcon(interview)}
                              <span className="font-medium truncate">
                                {interview.horaEntrevista}
                              </span>
                            </div>
                            <p className="truncate text-gray-700 mt-0.5">
                              {interview.candidate.fullName}
                            </p>
                          </div>
                        ))}
                        
                        {dayInterviews.length > 3 && (
                          <div className="text-xs text-gray-500 font-medium text-center py-1">
                            +{dayInterviews.length - 3} más
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Leyenda de colores */}
            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border-l-4 border-green-500"></div>
                <span className="text-gray-700">Aprobado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-100 border-l-4 border-yellow-500"></div>
                <span className="text-gray-700">Pendiente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100 border-l-4 border-red-500"></div>
                <span className="text-gray-700">Rechazado</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HRInterviewsCalendar
